import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { MainLayout } from '../../components/layout/MainLayout';
import { orderService } from '../../api/services/orderService';
import { orderNoteService, OrderNote } from '../../api/services/orderNoteService';
import { shippingMethodService, ShippingMethod } from '../../api/services/shippingMethodService';
import { returnService, ReturnAuthorization } from '../../api/services/returnService';
import { sewerPickupService, Sewer } from '../../api/services/sewerPickupService';
import { AddNoteModal } from '../../components/modals/AddNoteModal';
import { ItemActionHistoryModal } from '../../components/modals/ItemActionHistoryModal';
import CreateSupportTicketModal from '../../components/modals/CreateSupportTicketModal';

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [notes, setNotes] = useState<OrderNote[]>([]);
  const [returns, setReturns] = useState<ReturnAuthorization[]>([]);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState<boolean>(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [itemHistory, setItemHistory] = useState<any[]>([]);
  const [isEditingShipVia, setIsEditingShipVia] = useState<boolean>(false);
  const [editedShipVia, setEditedShipVia] = useState<string>('');
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [isEditingShipTo, setIsEditingShipTo] = useState<boolean>(false);
  const [shipToForm, setShipToForm] = useState({
    ShipToName: '',
    ShipToAddress1: '',
    ShipToAddress2: '',
    ShipToCity: '',
    ShipToState: '',
    ShipToZipCode: '',
    ShipToCountry: ''
  });
  const [savingShipTo, setSavingShipTo] = useState<boolean>(false);
  const [reassignItem, setReassignItem] = useState<any>(null);
  const [sewers, setSewers] = useState<Sewer[]>([]);
  const [selectedSewerId, setSelectedSewerId] = useState<number>(0);
  const [reassigning, setReassigning] = useState<boolean>(false);
  const [isSupportTicketModalOpen, setIsSupportTicketModalOpen] = useState<boolean>(false);

  useEffect(() => {
    loadOrderDetail();
    loadOrderNotes();
    loadOrderReturns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const loadOrderDetail = async () => {
    if (!orderId) return;

    setLoading(true);
    try {
      const result = await orderService.getOrderDetail(parseInt(orderId));
      setOrder(result);
    } catch (error) {
      console.error('Error loading order detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrderNotes = async () => {
    if (!orderId) return;

    try {
      const result = await orderNoteService.getOrderNotes(parseInt(orderId));
      setNotes(result);
    } catch (error) {
      console.error('Error loading order notes:', error);
    }
  };

  const loadOrderReturns = async () => {
    if (!orderId) return;

    try {
      const result = await returnService.getReturnsByOrderId(parseInt(orderId));
      setReturns(result);
    } catch (error) {
      console.error('Error loading order returns:', error);
    }
  };

  const openReassignModal = async (item: any) => {
    setReassignItem(item);
    setSelectedSewerId(0);
    if (sewers.length === 0) {
      try {
        const list = await sewerPickupService.getSewers();
        setSewers(list);
        if (list.length > 0) setSelectedSewerId(list[0].Id);
      } catch (error) {
        console.error('Error loading sewers:', error);
      }
    } else {
      setSelectedSewerId(sewers[0].Id);
    }
  };

  const handleReassign = async () => {
    if (!reassignItem || !selectedSewerId) return;
    setReassigning(true);
    try {
      await sewerPickupService.reassignSewer(reassignItem.Id, selectedSewerId);
      setReassignItem(null);
      await loadOrderDetail();
    } catch (error) {
      console.error('Error reassigning sewer:', error);
    } finally {
      setReassigning(false);
    }
  };

  const handleAddNote = async (noteText: string) => {
    if (!orderId) return;

    // Get the current user from localStorage or use a default
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const createBy = user.UserName || 'system';

    await orderNoteService.createOrderNote(parseInt(orderId), {
      Note: noteText,
      CreateBy: createBy
    });

    // Reload notes after adding
    await loadOrderNotes();
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!orderId) return;

    // Confirm before deleting
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await orderNoteService.deleteOrderNote(parseInt(orderId), noteId);
      // Reload notes after deleting
      await loadOrderNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const handlePartNumberClick = async (item: any) => {
    if (!orderId) return;

    try {
      // Get action history for the order
      const history = await orderService.getActionHistory(parseInt(orderId));

      // Filter history for this specific item
      const itemHistory = history[item.Id] || [];

      setSelectedItem(item);
      setItemHistory(itemHistory);
      setIsHistoryModalOpen(true);
    } catch (error) {
      console.error('Error loading item history:', error);
    }
  };

  const handleEditShipVia = async () => {
    setEditedShipVia(order.ShipVia || '');
    setIsEditingShipVia(true);

    // Load shipping methods if not already loaded
    if (shippingMethods.length === 0) {
      try {
        const methods = await shippingMethodService.getAllShippingMethods();
        setShippingMethods(methods);
      } catch (error) {
        console.error('Error loading shipping methods:', error);
      }
    }
  };

  const handleCancelEditShipVia = () => {
    setIsEditingShipVia(false);
    setEditedShipVia('');
  };

  const handleSaveShipVia = async () => {
    if (!orderId) return;

    try {
      await orderService.updateShippingMethod(parseInt(orderId), editedShipVia);
      // Reload order detail to get updated data
      await loadOrderDetail();
      setIsEditingShipVia(false);
    } catch (error) {
      console.error('Error updating shipping method:', error);
      alert('Failed to update shipping method');
    }
  };

  const handleEditShipTo = () => {
    setShipToForm({
      ShipToName: order.ShipToName || '',
      ShipToAddress1: order.ShipToAddress1 || '',
      ShipToAddress2: order.ShipToAddress2 || '',
      ShipToCity: order.ShipToCity || '',
      ShipToState: order.ShipToState || '',
      ShipToZipCode: order.ShipToZipCode || '',
      ShipToCountry: order.ShipToCountry || ''
    });
    setIsEditingShipTo(true);
  };

  const handleCancelEditShipTo = () => {
    setIsEditingShipTo(false);
  };

  const handleSaveShipTo = async () => {
    if (!orderId) return;
    if (!shipToForm.ShipToName || !shipToForm.ShipToAddress1 || !shipToForm.ShipToCity || !shipToForm.ShipToState || !shipToForm.ShipToZipCode) {
      alert('Name, Address 1, City, State, and Zip Code are required.');
      return;
    }
    setSavingShipTo(true);
    try {
      await orderService.updateShipTo(parseInt(orderId), shipToForm);
      await loadOrderDetail();
      setIsEditingShipTo(false);
    } catch (error) {
      console.error('Error updating ship to:', error);
      alert('Failed to update Ship To address.');
    } finally {
      setSavingShipTo(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!orderId) return;

    // Show confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to cancel this order? This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      // Get the current user
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const canceledBy = user.UserName || 'system';

      await orderService.cancelOrder(parseInt(orderId), canceledBy);

      // Reload order detail to show updated status
      await loadOrderDetail();

      alert('Order canceled successfully');
    } catch (error) {
      console.error('Error canceling order:', error);
      alert('Failed to cancel order');
    }
  };

  const handleReinstateOrder = async () => {
    if (!orderId) return;

    // Show confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to re-instate this order back to production?'
    );

    if (!confirmed) return;

    try {
      await orderService.reinstateOrder(parseInt(orderId));

      // Reload order detail to show updated status
      await loadOrderDetail();

      alert('Order re-instated successfully');
    } catch (error) {
      console.error('Error re-instating order:', error);
      alert('Failed to re-instate order');
    }
  };

  const calculateSubTotal = () => {
    if (!order?.Items) return 0;
    return order.Items.reduce((sum: number, item: any) => {
      if (item.PartNumber !== 'DISCOUNT') {
        return sum + (item.Price || 0);
      }
      return sum;
    }, 0);
  };

  const getDiscountAmount = () => {
    if (!order?.Items) return 0;
    const discountItem = order.Items.find((item: any) => item.PartNumber === 'DISCOUNT');
    return discountItem ? Math.abs(discountItem.Price || 0) : 0;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading order details...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!order) {
    return (
      <MainLayout>
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Order Not Found</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">The requested order could not be found.</p>
            <button
              onClick={() => navigate('/production/orders-dashboard')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Orders Dashboard
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const subTotal = calculateSubTotal();
  const discount = getDiscountAmount();
  const freight = order.Freight || 0;
  const tax = order.Tax || 0;
  const total = order.Total ?? (subTotal - discount + freight + tax);

  return (
    <MainLayout>
      <div className="flex-1 bg-gray-100 dark:bg-gray-900 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-6 print:pt-0 print:pb-2">
          {/* Header */}
          <div className="bg-gray-200 dark:bg-gray-800 px-6 py-4 mb-6 rounded-t-lg print:py-1 print:px-3">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Production Viewer</h1>
          </div>

          {/* Date and Buttons Row */}
          <div className="flex justify-between items-center mb-4 print:justify-end">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Date: {format(new Date(order.OrderDate), 'M/d/yyyy')}
            </div>
            <div className="flex gap-2 print:hidden">
              <button
                onClick={() => navigate('/production/orders-dashboard')}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Back to Orders
              </button>
              <button
                onClick={() => setIsSupportTicketModalOpen(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Create Support Ticket
              </button>
              <button
                onClick={() => navigate(`/production/orders/${orderId}/return`)}
                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
              >
                Create Return
              </button>
              {order.Closed === 0 ? (
                <button
                  onClick={handleCancelOrder}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Cancel this Order
                </button>
              ) : (
                <>
                  <button
                    disabled
                    className="px-4 py-2 bg-gray-400 text-gray-200 rounded cursor-not-allowed"
                  >
                    Order Closed
                  </button>
                  <button
                    onClick={handleReinstateOrder}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Re-Instate
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Sales Order # */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Sales Order #: {order.InvoiceNumber}
          </h2>

          {/* Customer and Ship To Section */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Customer */}
            <div>
              <div className="flex items-start gap-2 mb-2">
                <span className="font-semibold text-gray-900 dark:text-white">Customer:</span>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded">
                <div className="text-gray-800 dark:text-gray-200 font-semibold">{order.CustomerId}</div>
                <div className="text-gray-700 dark:text-gray-300">{order.BillToName}</div>
                {order.BillToAddress1 && <div className="text-gray-700 dark:text-gray-300">{order.BillToAddress1}</div>}
                {order.BillToAddress2 && <div className="text-gray-700 dark:text-gray-300">{order.BillToAddress2}</div>}
                <div className="text-gray-700 dark:text-gray-300">
                  {order.BillToCity}{order.BillToCity && ', '}{order.BillToState} {order.BillToZipCode}
                </div>
                {order.BillToCountry && <div className="text-gray-700 dark:text-gray-300">{order.BillToCountry}</div>}
                {order.OrderEmail && <div className="text-gray-700 dark:text-gray-300">{order.OrderEmail}</div>}
              </div>
            </div>

            {/* Ship To */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-gray-900 dark:text-white">Ship To:</span>
                {order.Closed === 0 && !isEditingShipTo && (
                  <button
                    onClick={handleEditShipTo}
                    className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 print:hidden"
                  >
                    Edit
                  </button>
                )}
              </div>
              {isEditingShipTo ? (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded space-y-2">
                  <div className="text-gray-700 dark:text-gray-300 text-sm">Drop Ship? {order.DropShip ? 'Yes' : 'No'}</div>
                  {[
                    { label: 'Name', key: 'ShipToName', required: true },
                    { label: 'Address 1', key: 'ShipToAddress1', required: true },
                    { label: 'Address 2', key: 'ShipToAddress2', required: false },
                    { label: 'City', key: 'ShipToCity', required: true },
                    { label: 'State', key: 'ShipToState', required: true },
                    { label: 'Zip Code', key: 'ShipToZipCode', required: true },
                    { label: 'Country', key: 'ShipToCountry', required: false }
                  ].map(({ label, key, required }) => (
                    <div key={key}>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}{required ? ' *' : ''}</label>
                      <input
                        type="text"
                        value={shipToForm[key as keyof typeof shipToForm]}
                        onChange={e => setShipToForm(f => ({ ...f, [key]: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleSaveShipTo}
                      disabled={savingShipTo}
                      className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                    >
                      {savingShipTo ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelEditShipTo}
                      disabled={savingShipTo}
                      className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded">
                  <div className="text-gray-700 dark:text-gray-300">Drop Ship? {order.DropShip ? 'Yes' : 'No'}</div>
                  <div className="text-gray-700 dark:text-gray-300">{order.ShipToName}</div>
                  {order.ShipToAddress1 && <div className="text-gray-700 dark:text-gray-300">{order.ShipToAddress1}</div>}
                  {order.ShipToAddress2 && <div className="text-gray-700 dark:text-gray-300">{order.ShipToAddress2}</div>}
                  <div className="text-gray-700 dark:text-gray-300">
                    {order.ShipToCity}{order.ShipToCity && ', '}{order.ShipToState} {order.ShipToZipCode}
                  </div>
                  {order.ShipToCountry && <div className="text-gray-700 dark:text-gray-300">{order.ShipToCountry}</div>}
                </div>
              )}
            </div>
          </div>

          {/* Order Details Row */}
          <div className="grid grid-cols-3 gap-6 mb-6 bg-white dark:bg-gray-800 p-4 rounded">
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">PO #</div>
              <div className="text-gray-700 dark:text-gray-300">{order.CustomerOrderNo || 'N/A'}</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-900 dark:text-white">Shipping Method</div>
              {order.Closed === 0 && !isEditingShipVia ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="text-gray-700 dark:text-gray-300">{order.ShipVia || 'N/A'}</div>
                  <button
                    onClick={handleEditShipVia}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 print:hidden"
                  >
                    Edit
                  </button>
                </div>
              ) : isEditingShipVia ? (
                <div className="flex items-center justify-center gap-2">
                  <select
                    value={editedShipVia}
                    onChange={(e) => setEditedShipVia(e.target.value)}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                  >
                    <option value="">Select a shipping method</option>
                    {shippingMethods.map((method) => (
                      <option key={method.Id} value={method.Name}>
                        {method.Name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleSaveShipVia}
                    className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEditShipVia}
                    className="px-3 py-1 text-xs bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="text-gray-700 dark:text-gray-300">{order.ShipVia || 'N/A'}</div>
              )}
            </div>
            <div className="text-right">
              <div className="font-semibold text-gray-900 dark:text-white">Payment Terms</div>
              <div className="text-gray-700 dark:text-gray-300">Net 30</div>
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                    Item #
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                    Part #
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                    Unit Price
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {order.Items?.map((item: any, index: number) => (
                  <tr key={item.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {order.InvoiceNumber}-{item.Ordinal}-{order.Items.length}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => handlePartNumberClick(item)}
                        className="text-blue-600 dark:text-blue-400 font-semibold hover:underline"
                      >
                        {item.PartNumber}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {item.Description}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">
                      ${item.Price?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-gray-900 dark:text-gray-100 font-semibold">
                        {item.StatusName || 'Unknown'}
                      </div>
                      {item.CreateDate && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {item.Status === 13
                            ? `Sewer Assignment on ${format(new Date(item.CreateDate), 'M/d/yyyy h:mm a')}`
                            : `In ${item.ActionName || item.StatusName} on ${format(new Date(item.CreateDate), 'M/d/yyyy h:mm a')}`}
                        </div>
                      )}
                      {item.Status === 13 && item.SewerName && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.SewerName}
                        </div>
                      )}
                      {item.Status === 13 && (
                        <button
                          onClick={() => openReassignModal(item)}
                          className="mt-1 text-xs px-2 py-0.5 border border-gray-400 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                        >
                          Re-assign
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals Section */}
            <div className="border-t border-gray-300 dark:border-gray-600">
              <div className="flex justify-end p-4">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">Sub Total:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">${subTotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">Discount:</span>
                      <span className="font-semibold text-red-600 dark:text-red-400">-${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t border-gray-300 dark:border-gray-600 pt-2">
                    <span className="text-gray-700 dark:text-gray-300">Freight:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">${freight.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">Tax:</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">${tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold border-t-2 border-gray-400 dark:border-gray-500 pt-2">
                    <span className="text-gray-900 dark:text-white">Total:</span>
                    <span className="text-gray-900 dark:text-white">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Shipping Notes */}
            <div>
              <div className="font-semibold text-gray-900 dark:text-white mb-2">Shipping Notes:</div>
              <div className="w-full min-h-[8rem] p-3 border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100">
                {order.ShippingNote || 'No shipping notes available.'}
              </div>
            </div>

            {/* Order Notes */}
            <div>
              <div className="font-semibold text-gray-900 dark:text-white mb-2">Order Notes:</div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded mb-4 min-h-[8rem]">
                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  {order.PendingConfirmedBy ? (
                    <div>Confirmed from Pending Orders by <strong>{order.PendingConfirmedBy}</strong>{order.PendingConfirmedDate ? ` on ${format(new Date(order.PendingConfirmedDate), 'M/d/yyyy h:mm a')}` : ''}</div>
                  ) : order.EnteredBy ? (
                    <div>Entered via New Order by <strong>{order.EnteredBy}</strong></div>
                  ) : null}
                  {order.CreateBy && order.CreateDate && (
                    <div>Sent to Production by <strong>{order.CreateBy}</strong> on {format(new Date(order.CreateDate), 'M/d/yyyy h:mm a')}</div>
                  )}
                  {order.Note && (
                    <div className="pt-1 text-gray-600 dark:text-gray-400">Note: {order.Note}</div>
                  )}
                  {!order.PendingConfirmedBy && !order.EnteredBy && !order.CreateBy && !order.Note && (
                    <div className="text-gray-400 dark:text-gray-500">No notes.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Confirmation Notes Section */}
          {order.ConfirmationNotes && order.ConfirmationNotes.length > 0 && (
            <div className="mb-6">
              <div className="font-semibold text-gray-900 dark:text-white mb-2">Confirmation Notes:</div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                        Comment
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600 w-36">
                        By
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600 w-40">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {order.ConfirmationNotes.map((cn: any, idx: number) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {cn.Comment || <span className="text-gray-400 dark:text-gray-500">—</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {cn.CreateBy || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {cn.CreateDate ? format(new Date(cn.CreateDate), 'M/d/yy h:mm a') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tracking Details Section */}
          {order.TrackingDetails && order.TrackingDetails.length > 0 && (
            <div className="mb-6">
              <div className="font-semibold text-gray-900 dark:text-white mb-2">Tracking Details:</div>
              <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded">
                {order.TrackingDetails.map((tracking: any, index: number) => (
                  <div
                    key={index}
                    className={`p-4 text-sm ${
                      index !== order.TrackingDetails.length - 1
                        ? 'border-b border-gray-300 dark:border-gray-600'
                        : ''
                    }`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-gray-700 dark:text-gray-300">
                      <div>
                        <span className="font-semibold">Shipped on:</span>{' '}
                        {tracking.ShipmentDate ? format(new Date(tracking.ShipmentDate), 'M/d/yy h:mm a') : '-'}
                      </div>
                      <div>
                        <span className="font-semibold">Tracking #:</span> {tracking.TrackingNumber || '-'}
                      </div>
                      <div>
                        <span className="font-semibold">Service:</span> {tracking.Service || '-'}
                      </div>
                      <div>
                        <span className="font-semibold">Number of Packages:</span> {tracking.NumberOfPackages || '-'}
                      </div>
                      <div>
                        <span className="font-semibold">Weight:</span> {tracking.TotalWeight || '-'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Returns / Replacements Section */}
          {returns.length > 0 && (
            <div className="mb-6">
              <div className="font-semibold text-gray-900 dark:text-white mb-2">Returns / Replacements for this Order:</div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                        Authorization #
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                        Tag
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                        Credit Memo
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {returns.map((returnAuth) => (
                      <tr key={returnAuth.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => navigate(`/production/returns/${returnAuth.Id}`)}
                            className="text-blue-600 dark:text-blue-400 font-semibold hover:underline"
                          >
                            {returnAuth.AuthorizationNumber}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {returnAuth.ReturnType === 1 ? 'Repair' : 'Return'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {returnAuth.ReturnTag || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {format(new Date(returnAuth.CreateDate), 'M/d/yyyy h:mm a')}
                          <div className="text-xs text-gray-500 dark:text-gray-400">by {returnAuth.CreateBy}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {returnAuth.CreditMemo || <span className="text-gray-400 dark:text-gray-500">—</span>}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {returnAuth.CompleteDate ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              Completed
                            </span>
                          ) : returnAuth.ReceiveDate ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              Received
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                              Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                    Note
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                    Entered by
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600 print:hidden">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {notes.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No notes yet. Click "Add Note" to create one.
                    </td>
                  </tr>
                ) : (
                  notes.map((note) => (
                    <tr key={note.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {note.Note}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {note.CreateBy}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {format(new Date(note.CreateDate), 'M/d/yy h:mm a')}
                      </td>
                      <td className="px-4 py-3 text-sm print:hidden">
                        <button
                          onClick={() => handleDeleteNote(note.Id)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Add Note Button */}
          <div className="mb-6 print:hidden">
            <button
              onClick={() => setIsAddNoteModalOpen(true)}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
            >
              Add Note
            </button>
          </div>
        </div>
      </div>

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        onSubmit={handleAddNote}
        currentUser={JSON.parse(localStorage.getItem('user') || '{}').UserName || 'system'}
      />

      {/* Item Action History Modal */}
      <ItemActionHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        partNumber={selectedItem?.PartNumber || ''}
        history={itemHistory}
      />

      {/* Sewer Re-assignment Modal */}
      {reassignItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-80">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Sewer re-assignment
            </h2>
            <div className="flex gap-2">
              <select
                value={selectedSewerId}
                onChange={e => setSelectedSewerId(Number(e.target.value))}
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value={0} disabled>Select sewer…</option>
                {sewers.map(s => (
                  <option key={s.Id} value={s.Id}>
                    {s.FirstName} {s.LastName}
                  </option>
                ))}
              </select>
              <button
                onClick={handleReassign}
                disabled={!selectedSewerId || reassigning}
                className="px-4 py-1.5 rounded text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
              >
                {reassigning ? 'Saving…' : 'Assign'}
              </button>
            </div>
            <button
              onClick={() => setReassignItem(null)}
              className="mt-3 text-xs text-gray-500 dark:text-gray-400 hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Create Support Ticket Modal */}
      {isSupportTicketModalOpen && order && (
        <CreateSupportTicketModal
          orderId={parseInt(orderId!)}
          orderNumber={order.InvoiceNumber}
          onClose={() => setIsSupportTicketModalOpen(false)}
        />
      )}
    </MainLayout>
  );
};

export default OrderDetail;
